import { useState, useEffect } from 'react';
import api from '../services/api';
import Text from '../components/shared/Text';

// Premium Stat Card - Refined & Professional
function StatCard({ title, value, subtext, icon, color = "indigo", trend }: any) {
    const themes: any = {
        indigo: { bg: 'bg-indigo-50/30', border: 'border-indigo-100', iconBg: 'bg-indigo-100 text-indigo-600', shadow: 'shadow-sm' },
        emerald: { bg: 'bg-emerald-50/30', border: 'border-emerald-100', iconBg: 'bg-emerald-100 text-emerald-600', shadow: 'shadow-sm' },
        purple: { bg: 'bg-purple-50/30', border: 'border-purple-100', iconBg: 'bg-purple-100 text-purple-600', shadow: 'shadow-sm' },
        rose: { bg: 'bg-rose-50/30', border: 'border-rose-100', iconBg: 'bg-rose-100 text-rose-600', shadow: 'shadow-sm' }
    };

    const t = themes[color] || themes.indigo;

    return (
        <div className={`p-6 rounded-2xl border ${t.border} ${t.bg} ${t.shadow} transition-all duration-300 hover:shadow-md group`}>
            <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 ${t.iconBg} rounded-xl flex items-center justify-center text-xl shadow-sm transition-transform duration-300 group-hover:scale-105`}>
                    {icon}
                </div>
                {trend && (
                    <div className={`px-2 py-0.5 rounded-full text-[10px] font-black tracking-widest flex items-center gap-1 border ${trend > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-100'}`}>
                        {trend > 0 ? '↗' : '↘'} {Math.abs(trend)}%
                    </div>
                )}
            </div>
            
            <div className="space-y-1">
                <Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">{title}</Text>
                <Text variant="h1" color="primary" className="text-3xl">{value}</Text>
                <Text variant="small" color="slate" className="italic opacity-50">{subtext}</Text>
            </div>
        </div>
    );
}

// Sophisticated SVG Line Chart - Cleaner Version
function LineChart({ data, label, color = "indigo" }: any) {
    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="h-64 flex flex-col items-center justify-center text-slate-300">
                <Text variant="label" color="slate" className="uppercase tracking-widest opacity-40">No Intelligence Data</Text>
            </div>
        );
    }

    const entries = Object.entries(data).sort(([a]: any, [b]: any) => new Date(a).getTime() - new Date(b).getTime());
    const values: any = entries.map(([, v]) => v);
    const max = Math.max(...values, 1);
    const min = 0;
    const range = max - min || 1;

    const width = 100;
    const height = 100;
    const points = entries.map(([, value]: any, i) => {
        const x = (i / (entries.length - 1 || 1)) * width;
        const y = height - ((value - min) / range) * height;
        return `${x},${y}`;
    }).join(' ');

    const themes: any = {
        indigo: { stroke: '#6366f1' },
        emerald: { stroke: '#10b981' },
        rose: { stroke: '#f43f5e' }
    };

    const theme = themes[color] || themes.indigo;

    return (
        <div>
            <div className="flex justify-between items-end mb-8">
                <div>
                    <Text variant="label" color="slate" className="uppercase tracking-widest opacity-60 mb-1">{label}</Text>
                    <Text variant="h3" color="primary">Performance Flux</Text>
                </div>
                <div className="text-right">
                    <Text variant="small" weight="bold" color="primary" className="px-3 py-1 bg-slate-50 border border-slate-100 rounded-lg">
                        {max.toLocaleString()} Peak
                    </Text>
                </div>
            </div>

            <div className="h-48 relative">
                <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible" preserveAspectRatio="none">
                    <polyline
                        points={points}
                        fill="none"
                        stroke={theme.stroke}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>
        </div>
    );
}

// Status Badge with refined interaction
function StatusBadge({ status, inquiryId, onStatusChange }: any) {
    const [isOpen, setIsOpen] = useState(false);
    const [updating, setUpdating] = useState(false);

    const statuses = ['pending', 'read', 'replied', 'archived'];
    const statusThemes: any = {
        pending: 'bg-amber-50 text-amber-700 border-amber-200',
        read: 'bg-blue-50 text-blue-700 border-blue-200',
        replied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        archived: 'bg-slate-50 text-slate-700 border-slate-200'
    };

    const handleStatusChange = async (newStatus: string) => {
        setUpdating(true);
        try {
            const response = await api.patch(`/inquiries/${inquiryId}/status`, { status: newStatus });
            if (response.success) {
                onStatusChange(inquiryId, newStatus);
            }
        } catch (error) {
            console.error('Failed to update status:', error);
        } finally {
            setUpdating(false);
            setIsOpen(false);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                disabled={updating}
                className={`relative px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all duration-300 ${statusThemes[status]} active:scale-95 flex items-center gap-2`}
            >
                <span className={`w-1.5 h-1.5 rounded-full ${status === 'pending' ? 'bg-amber-500 animate-pulse' : 'bg-current opacity-40'}`}></span>
                {updating ? 'SYCNING...' : status}
            </button>
            {isOpen && (
                <>
                    <div className="fixed inset-0 z-[60]" onClick={() => setIsOpen(false)} />
                    <div className="absolute right-0 bottom-full mb-3 w-44 bg-white rounded-2xl shadow-xl border border-slate-100 z-[70] p-2 overflow-hidden">
                        {statuses.map(s => (
                            <button
                                key={s}
                                onClick={() => handleStatusChange(s)}
                                className={`w-full text-left px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${s === status ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

// Main Premium Dashboard
function AnalyticsDashboard() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [period, setPeriod] = useState('30');

    useEffect(() => {
        loadAnalytics();
    }, [period]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const response = await api.get('/analytics/dashboard', { period });
            if (response.success) {
                setData(response.data);
            }
        } catch (error) {
            console.error('Failed to load analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (inquiryId: any, newStatus: string) => {
        setData((prev: any) => ({
            ...prev,
            recent_inquiries: prev.recent_inquiries.map((inq: any) =>
                inq.id === inquiryId ? { ...inq, status: newStatus } : inq
            )
        }));
    };

    const exportToCSV = () => {
        if (!data?.recent_inquiries) return;

        const headers = ['Name', 'Phone', 'Email', 'Product', 'Message', 'Status', 'Date'];
        const rows = data.recent_inquiries.map((inq: any) => [
            inq.name,
            inq.phone,
            inq.email || '',
            inq.product_name,
            inq.message || '',
            inq.status,
            new Date(inq.created_at).toLocaleString()
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map((row: any[]) => row.map((cell: any) => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `inventory-intel-export-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading && !data) {
        return (
            <div className="min-h-[80vh] flex flex-col items-center justify-center space-y-4">
                <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                <Text variant="label" color="slate" className="uppercase tracking-widest opacity-40">Orchestrating Intelligence...</Text>
            </div>
        );
    }

    if (!data) return null;

    return (
    <div className="pt-6 space-y-12 animate-in fade-in duration-700">
      {/* Premium Header Section */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-8 pb-4 border-b border-slate-100">
        <div className="space-y-1">
          <Text variant="h1" color="primary">
            Business <span className="text-indigo-600">Intelligence</span>
          </Text>
          <div className="flex items-center gap-2">
             <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
             <Text variant="body" color="slate" weight="medium">
                Real-time operational metrics and commercial trajectory mapping
             </Text>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-white p-2 rounded-[1.5rem] border-2 border-slate-50 shadow-sm">
          {[
            { value: '7', label: '7D' },
            { value: '30', label: '30D' },
            { value: '90', label: '90D' },
            { value: 'all', label: 'MAX' }
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setPeriod(value)}
              className={`px-6 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${period === value
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

            {/* Performance Hub */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-20">
                <StatCard
                    title="Terminal Hits"
                    value={data.total_visitors.toLocaleString()}
                    subtext={`${data.unique_visitors} unique agent pings`}
                    color="indigo"
                    trend={+12.4}
                    icon="🛰️"
                />
                <StatCard
                    title="Conversion Leads"
                    value={data.total_queries.toLocaleString()}
                    subtext="High-intent product inquiries"
                    color="emerald"
                    trend={+8.2}
                    icon="📡"
                />
                <StatCard
                    title="Engagement Ratio"
                    value={`${data.conversion_rate}%`}
                    subtext="Trajectory efficiency index"
                    color="purple"
                    trend={-2.1}
                    icon="🎯"
                />
                <StatCard
                    title="Market Velocity"
                    value="Optimal"
                    subtext="Mean transaction processing"
                    color="rose"
                    icon="⚡"
                />
            </div>

            {/* Navigation Matrix */}
            <div className="flex flex-col xl:flex-row gap-8 mb-12 border-b border-slate-100 relative z-10">
                <div className="flex overflow-x-auto no-scrollbar gap-8">
                    {[
                        { id: 'overview', label: 'Operational Overview', icon: '💎' },
                        { id: 'inquiries', label: 'Lead Management', icon: '🔋' },
                        { id: 'visitors', label: 'Audience Insight', icon: '🌊' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`group flex items-center gap-3 px-1 py-5 text-[11px] font-black uppercase tracking-widest transition-all relative ${activeTab === tab.id
                                ? 'text-indigo-600'
                                : 'text-slate-400 hover:text-slate-900'
                                }`}
                        >
                            <span className="text-xl">{tab.icon}</span>
                            {tab.label}
                            {activeTab === tab.id && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full"></div>
                            )}
                        </button>
                    ))}
                </div>

                <div className="xl:ml-auto flex items-center gap-4 pb-4">
                    <button
                        onClick={exportToCSV}
                        className="px-6 py-3 bg-slate-900 text-white rounded-xl shadow-sm text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all flex items-center gap-2"
                    >
                        <span>📊</span> Download Registry
                    </button>
                </div>
            </div>

            {/* Intelligence Content */}
            <div className="animate-in fade-in duration-700">
                {activeTab === 'overview' && (
                    <div className="space-y-12">
                        {/* Flow Architectures */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 hover:shadow-md transition-all">
                                <LineChart data={data.daily_visitors} label="Visitor Trajectory" color="indigo" />
                            </div>
                            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 hover:shadow-md transition-all">
                                <LineChart data={data.daily_inquiries} label="Lead Generation Flux" color="emerald" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                            {/* Apex Assets */}
                            <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                                    <div>
                                        <Text variant="h3" color="primary">Performance Ranking</Text>
                                        <Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Highest yield operational units</Text>
                                    </div>
                                    <div className="w-10 h-10 bg-white border border-slate-100 text-indigo-600 rounded-xl flex items-center justify-center text-xl shadow-sm">🥇</div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="bg-slate-50/30">
                                                <th className="px-6 py-4"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Strategic Asset</Text></th>
                                                <th className="px-6 py-4 text-center"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Hits</Text></th>
                                                <th className="px-6 py-4 text-center"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Conversions</Text></th>
                                                <th className="px-6 py-4 text-right"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Efficiency</Text></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {data.top_products.map((product: any, idx: number) => (
                                                <tr key={product.id} className="group hover:bg-slate-50/50 transition-all duration-300">
                                                    <td className="px-6 py-5">
                                                       <div className="flex items-center gap-3">
                                                           <Text variant="mono" color="slate" className="text-xs opacity-50">#{idx+1}</Text>
                                                           <Text weight="bold" color="primary" className="group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{product.name}</Text>
                                                       </div>
                                                    </td>
                                                    <td className="px-6 py-5 text-center">
                                                        <Text variant="body" color="slate" weight="bold">{product.views.toLocaleString()}</Text>
                                                    </td>
                                                    <td className="px-6 py-5 text-center">
                                                        <Text variant="body" weight="bold" className="text-emerald-600">{product.queries.toLocaleString()}</Text>
                                                    </td>
                                                    <td className="px-6 py-5 text-right">
                                                        <Text variant="small" weight="bold" className="inline-block px-3 py-1 bg-indigo-50 text-indigo-700 rounded-lg">
                                                            {product.views > 0 ? ((product.queries / product.views) * 100).toFixed(1) : 0}%
                                                        </Text>
                                                    </td>
                                                </tr>
                                            ))}
                                            {data.top_products.length === 0 && (
                                                <tr>
                                                    <td colSpan={4} className="px-6 py-20 text-center">
                                                        <Text variant="body" color="slate" className="italic opacity-50">No operational data recorded yet.</Text>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Terminal Matrix */}
                            <div className="bg-slate-900 rounded-2xl p-8 group relative overflow-hidden shadow-xl shadow-slate-200">
                                <div className="relative z-10 space-y-8">
                                    <div>
                                        <Text variant="h3" color="white">Terminal Mix</Text>
                                        <Text variant="label" color="slate" className="uppercase tracking-widest opacity-60 mt-1">Architecture distribution</Text>
                                    </div>
                                    
                                    <div className="space-y-6">
                                        {Object.entries(data.device_stats).map(([device, count]: any) => {
                                            const total: any = Object.values(data.device_stats).reduce((a: any, b: any) => a + b, 0);
                                            const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;

                                            return (
                                                <div key={device}>
                                                    <div className="flex justify-between items-end mb-3">
                                                        <Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">
                                                            {device === 'desktop' ? '🖥️' : '📱'} {device}
                                                        </Text>
                                                        <Text variant="body" color="white" weight="bold">{percentage}%</Text>
                                                    </div>
                                                    <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-1000 ${device === 'mobile' ? 'bg-indigo-500' : 'bg-white'}`}
                                                            style={{ width: `${percentage}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    
                                    <div className="pt-8 border-t border-white/5 flex items-center justify-between">
                                        <div>
                                            <Text variant="label" color="slate" className="uppercase tracking-widest opacity-40 mb-1">Total Pings</Text>
                                            <Text variant="h2" color="white">{(Object.values(data.device_stats) as number[]).reduce((a: number, b: number) => a + b, 0).toLocaleString()}</Text>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'inquiries' && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="p-8 border-b border-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                            <div>
                                <Text variant="h2" color="primary">Lead Intelligence</Text>
                                <Text variant="label" color="slate" className="uppercase tracking-widest opacity-60 mt-1">Historical intent capture log</Text>
                            </div>
                            <div className="px-4 py-2 bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-xl flex items-center gap-3">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                <Text variant="label" color="slate" className="uppercase tracking-widest text-emerald-700">Live Operations</Text>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-slate-50/30">
                                        <th className="px-8 py-4"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Identity</Text></th>
                                        <th className="px-6 py-4"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Objective</Text></th>
                                        <th className="px-6 py-4"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Payload</Text></th>
                                        <th className="px-6 py-4"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Chronology</Text></th>
                                        <th className="px-8 py-4 text-right"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Status</Text></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.recent_inquiries?.map((inquiry: any) => (
                                        <tr key={inquiry.id} className="group hover:bg-slate-50/50 transition-all">
                                            <td className="px-8 py-6">
                                                <Text weight="bold" color="primary" className="uppercase tracking-tight group-hover:text-indigo-600 transition-colors">{inquiry.name}</Text>
                                                <Text variant="small" color="slate" weight="bold" className="mt-0.5 opacity-50">{inquiry.phone}</Text>
                                            </td>
                                            <td className="px-6 py-6">
                                                <span className="px-2.5 py-1 bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-full text-[9px] font-black uppercase tracking-widest">
                                                    {inquiry.product_name}
                                                </span>
                                            </td>
                                            <td className="px-6 py-6 max-w-sm">
                                                <Text variant="small" color="slate" className="italic line-clamp-1 opacity-70">
                                                    {inquiry.message || 'Standard intent established...'}
                                                </Text>
                                            </td>
                                            <td className="px-6 py-6">
                                                <Text variant="small" color="slate" weight="bold" className="opacity-50">
                                                    {new Date(inquiry.created_at).toLocaleDateString()}
                                                </Text>
                                            </td>
                                            <td className="px-8 py-6 text-right">
                                                <StatusBadge
                                                    status={inquiry.status}
                                                    inquiryId={inquiry.id}
                                                    onStatusChange={handleStatusChange}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                    {(!data.recent_inquiries || data.recent_inquiries.length === 0) && (
                                        <tr>
                                            <td colSpan={5} className="px-8 py-20 text-center">
                                                <Text variant="body" color="slate" className="italic opacity-50">No commercial pings recorded.</Text>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'visitors' && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="p-8 border-b border-slate-50">
                            <Text variant="h2" color="primary">Terminal Analytics</Text>
                            <Text variant="label" color="slate" className="uppercase tracking-widest opacity-60 mt-1">Behavioral footprint processing</Text>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-slate-50/30">
                                        <th className="px-8 py-4"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Signature</Text></th>
                                        <th className="px-6 py-4 text-center"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Sessions</Text></th>
                                        <th className="px-6 py-4"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Trajectory Points</Text></th>
                                        <th className="px-6 py-4"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Interface</Text></th>
                                        <th className="px-8 py-4 text-right"><Text variant="label" color="slate" className="uppercase tracking-widest opacity-60">Last Ping</Text></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.recent_visitors?.map((visitor: any) => (
                                        <tr key={visitor.visitor_id} className="group hover:bg-slate-50/50 transition-all">
                                            <td className="px-8 py-6">
                                                <Text variant="mono" color="slate" className="text-[10px] opacity-60 group-hover:text-indigo-600 transition-colors uppercase">
                                                    ID_{visitor.visitor_id.slice(0, 10)}
                                                </Text>
                                            </td>
                                            <td className="px-6 py-6 text-center">
                                                <Text variant="small" weight="bold" color="primary" className="bg-slate-100 rounded-lg px-3 py-1">
                                                    {visitor.visit_count}
                                                </Text>
                                            </td>
                                            <td className="px-6 py-6">
                                                <Text variant="mono" color="slate" className="text-[10px] uppercase opacity-60">
                                                    {visitor.products_viewed.slice(0, 2).join(', ') || 'NONE'}
                                                </Text>
                                            </td>
                                            <td className="px-6 py-6">
                                                <span className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-lg text-[10px] font-black uppercase tracking-widest text-slate-600 group-hover:bg-slate-900 group-hover:text-white transition-all">
                                                    {visitor.device_type === 'desktop' ? '🖥️' : '📲'} {visitor.device_type}
                                                </span>
                                            </td>
                                            <td className="px-8 py-6 text-right">
                                                <Text variant="small" color="slate" weight="bold" className="opacity-50">
                                                    {new Date(visitor.last_visit).toLocaleDateString()}
                                                </Text>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AnalyticsDashboard;
